import React, { useMemo } from 'react';

import { ArcData, describeArc, describeInnerSemicircle, getOverlayCirclePosition } from './gaugeUtils';

type GaugeSvgProps = {
  id: string;
  size: number;
  innerRadius: number;
  chartData: ArcData[];
  adjustedHealthPercentage: number;
  pathFillColor: string;
  backgroundFillColor?: string;
};

const GaugeSvg: React.FC<GaugeSvgProps> = ({
  id,
  size,
  innerRadius,
  chartData,
  adjustedHealthPercentage,
  pathFillColor,
  backgroundFillColor,
}) => {
  const center = size / 2;
  const radius = (size - 42) / 2;
  // Bottom buffer allows overlay circle to be visible when outside of visible gauge arch
  const bottomBuffer = 20;

  const paths = useMemo(() => {
    const total = chartData.reduce((sum, item) => sum + item.value, 0);

    let currentAngle = -180;
    const gap = 2;
    const chartSpan = 180 + gap;

    // Filter out items with value outside visible gauge arch to prevent rendering zero-length arcs
    return chartData
      .filter((item) => item.value > gap)
      .map(({ value, color }, index) => {
        const angle = (value / total) * chartSpan;
        const arc = describeArc(center, center, radius, currentAngle, currentAngle + angle, gap);
        currentAngle += angle;
        return <path key={`${id}-slice-${index}`} d={arc} fill={color} stroke='none' />;
      });
  }, [chartData, center, radius, id]);

  const innerSemicirclePath = useMemo(() => describeInnerSemicircle(center, innerRadius), [center, innerRadius]);

  const overlayCircleProps = useMemo(
    () => getOverlayCirclePosition(center, radius, adjustedHealthPercentage, size),
    [center, radius, adjustedHealthPercentage],
  );

  return (
    <svg width={size} height={center + bottomBuffer} viewBox={`0 0 ${size} ${center + bottomBuffer}`}>
      <g>
        {paths}
        <path d={innerSemicirclePath} fill={backgroundFillColor ?? 'var(--color-background-pageBG)'} />
        {overlayCircleProps && (
          <circle
            {...overlayCircleProps}
            fill={pathFillColor}
            stroke='var(--color-border-inverse)'
            strokeWidth={6}
            className='drop-shadow'
          />
        )}
      </g>
    </svg>
  );
};

export default GaugeSvg;
